/**
 * InfoPage Component
 * Generic informational page for About, Contact, Support, etc.
 * Fetches content from Shopify Pages API with fallback to hardcoded content.
 */

import { useState, useEffect } from 'react';
import { Layout } from '~/components/layout/Layout';
import { motion } from 'motion/react';
import {
  IconSupport,
  IconShippingFast,
  IconReturn,
  IconShieldCheck,
  IconHeadset,
  IconArrowRight,
  IconChevronDown,
  IconLock,
  IconFileText,
  IconCookie,
  IconDatabase,
  IconEye,
  IconMail,
  IconCreditCard,
  IconGlobe,
  IconAlertCircle,
  IconInfo,
  IconCheck,
  IconClock,
  IconPackage,
  IconMapPin,
  IconPhone,
} from '~/components/ui/Icons';
import { getPageByHandle, type ShopifyPage } from '~/lib/shopify/api';

interface InfoPageProps {
  slug: string;
}

// ============================================================================
// REUSABLE POLICY PAGE COMPONENTS
// ============================================================================

// Accordion Section Component
function PolicyAccordion({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
  number
}: {
  title: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
  number?: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-surface-slate/30 rounded-2xl overflow-hidden bg-surface-coal/50 backdrop-blur-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-surface-slate/10 transition-colors"
      >
        {number && (
          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-orange/10 text-brand-orange text-sm font-bold flex items-center justify-center">
            {number}
          </span>
        )}
        {Icon && !number && (
          <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-orange/10 text-brand-orange flex items-center justify-center">
            <Icon size={20} />
          </span>
        )}
        <span className="flex-1 font-semibold text-accent-white">{title}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-accent-silver"
        >
          <IconChevronDown size={20} />
        </motion.span>
      </button>
      {/* Content always rendered in DOM for SEO/no-JS accessibility */}
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}
        aria-hidden={!isOpen}
      >
        <div className="px-5 pb-5 pt-0">
          <div className="pl-0 md:pl-14 text-accent-silver text-[15px] leading-relaxed space-y-4">
            {children}
          </div>
        </div>
      </div>
      {/* noscript fallback: show all content when JS is disabled */}
      <noscript>
        <style dangerouslySetInnerHTML={{ __html: '[aria-hidden="true"] { max-height: none !important; opacity: 1 !important; }' }} />
      </noscript>
    </div>
  );
}

// Highlight Card Component
function HighlightCard({
  icon: Icon,
  title,
  description,
  variant = 'default'
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  description: string;
  variant?: 'default' | 'warning' | 'success';
}) {
  const variants = {
    default: 'bg-surface-coal border-surface-slate/30',
    warning: 'bg-amber-500/5 border-amber-500/20',
    success: 'bg-emerald-500/5 border-emerald-500/20',
  };
  const iconColors = {
    default: 'text-brand-orange bg-brand-orange/10',
    warning: 'text-amber-400 bg-amber-500/10',
    success: 'text-emerald-400 bg-emerald-500/10',
  };

  return (
    <div className={`p-5 rounded-2xl border ${variants[variant]}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${iconColors[variant]}`}>
        <Icon size={22} />
      </div>
      <h3 className="font-semibold text-accent-white mb-1">{title}</h3>
      <p className="text-sm text-accent-silver leading-relaxed">{description}</p>
    </div>
  );
}

// Key Stats Row
function KeyStats({ stats }: { stats: { value: string; label: string }[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
      {stats.map((stat, i) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
          className="relative p-5 rounded-2xl bg-gradient-to-br from-surface-coal to-surface-graphite border border-surface-slate/30 text-center overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="text-2xl sm:text-3xl font-display font-bold text-brand-orange mb-1">{stat.value}</div>
            <div className="text-xs sm:text-sm text-accent-silver">{stat.label}</div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// Info Box Component
function InfoBox({
  title,
  children,
  variant = 'info'
}: {
  title?: string;
  children: React.ReactNode;
  variant?: 'info' | 'warning' | 'tip';
}) {
  const styles = {
    info: 'bg-brand-orange/5 border-brand-orange/20 text-brand-orange',
    warning: 'bg-amber-500/5 border-amber-500/20 text-amber-400',
    tip: 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400',
  };
  const icons = {
    info: IconInfo,
    warning: IconAlertCircle,
    tip: IconCheck,
  };
  const Icon = icons[variant];

  return (
    <div className={`p-4 rounded-xl border ${styles[variant].split(' ').slice(0, 2).join(' ')} flex gap-3`}>
      <Icon size={20} className={`flex-shrink-0 mt-0.5 ${styles[variant].split(' ').slice(2).join(' ')}`} />
      <div className="space-y-1">
        {title && <div className="font-medium text-accent-white text-sm">{title}</div>}
        <div className="text-sm text-accent-silver">{children}</div>
      </div>
    </div>
  );
}

// Styled Table Component
function PolicyTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-surface-slate/30">
      <table className="w-full">
        <thead>
          <tr className="bg-surface-graphite">
            {headers.map((header, i) => (
              <th key={i} className="text-left py-3 px-4 text-sm font-semibold text-accent-white first:rounded-tl-xl last:rounded-tr-xl">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-surface-slate/20 hover:bg-surface-slate/5 transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="py-3 px-4 text-sm text-accent-silver">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Checklist Item
function ChecklistItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center mt-0.5">
        <IconCheck size={12} />
      </span>
      <span>{children}</span>
    </li>
  );
}

// Process Steps
function ProcessSteps({ steps }: { steps: { title: string; description: string }[] }) {
  return (
    <div className="space-y-4">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-orange text-white text-sm font-bold flex items-center justify-center">
            {i + 1}
          </div>
          <div className="flex-1 pt-1">
            <div className="font-medium text-accent-white mb-1">{step.title}</div>
            <div className="text-sm text-accent-silver">{step.description}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Contact Card
function ContactCard({ icon: Icon, label, value, href }: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className="flex items-center gap-4 p-4 bg-surface-coal rounded-xl border border-surface-slate/30 hover:border-brand-orange/40 transition-all group"
    >
      <div className="w-11 h-11 bg-brand-orange/10 rounded-xl flex items-center justify-center group-hover:bg-brand-orange/20 transition-colors">
        <Icon size={20} className="text-brand-orange" />
      </div>
      <div>
        <div className="text-xs text-accent-silver uppercase tracking-wider">{label}</div>
        <div className="text-accent-white font-medium">{value}</div>
      </div>
    </a>
  );
}

// ============================================================================
// PAGE CONTENT CONFIGURATION
// ============================================================================

const pageContent: Record<string, {
  title: string;
  subtitle?: string;
  content: React.ReactNode;
}> = {
  about: {
    title: 'About Us',
    subtitle: 'UK specialist in professional headsets',
    content: (
      <div className="prose prose-invert max-w-none">
        <p className="text-lg text-accent-silver leading-relaxed">
          IT Remarketing is a specialist retailer of professional
          communication headsets. We stock Jabra, Poly, and Axtel, bringing
          you genuine products at competitive prices.
        </p>

        <div className="grid md:grid-cols-3 gap-6 my-12 not-prose">
          {[
            { number: 'Specialist', label: 'UK Retailer' },
            { number: '30-Day', label: 'Free Returns' },
          ].map((stat) => (
            <div key={stat.label} className="text-center p-6 bg-surface-coal rounded-2xl border border-surface-slate/30">
              <div className="text-4xl font-display font-bold text-brand-orange">{stat.number}</div>
              <div className="text-sm text-accent-silver mt-2">{stat.label}</div>
            </div>
          ))}
        </div>

        <h2>Our Mission</h2>
        <p>
          We believe everyone deserves crystal-clear communication. Whether you're in a busy
          call centre, a home office, or a corporate boardroom, we'll help you find the
          perfect headset for your needs.
        </p>

        <h2>Why Choose Us?</h2>
        <ul>
          <li>Specialist in Jabra, Poly, and Axtel headsets</li>
          <li>Helpful advice to find the right headset</li>
          <li>Free next-day delivery on orders over £75</li>
          <li>30-day hassle-free returns</li>
          <li>2-year warranty on all products</li>
        </ul>
      </div>
    ),
  },
  contact: {
    title: 'Contact Us',
    subtitle: 'We\'re here to help',
    content: (
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <p className="text-lg text-accent-silver mb-8">
            Have a question about our products or need help choosing the right headset?
            Our team of experts is ready to assist you.
          </p>

          <div className="space-y-6">
            {[
              { icon: IconSupport, label: 'Phone', value: '020 4591 5420', href: 'tel:02045915420' },
              { icon: IconHeadset, label: 'Email', value: 'sales@itremarketing.co.uk', href: 'mailto:sales@itremarketing.co.uk' },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="flex items-center gap-4 p-4 bg-surface-coal rounded-xl border border-surface-slate/30 hover:border-brand-orange/50 transition-colors"
              >
                <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center">
                  <item.icon size={24} className="text-brand-orange" />
                </div>
                <div>
                  <div className="text-sm text-accent-silver">{item.label}</div>
                  <div className="text-accent-white font-medium">{item.value}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-8 p-6 bg-surface-coal rounded-xl border border-surface-slate/30">
            <h3 className="font-semibold text-accent-white mb-2">Business Hours</h3>
            <p className="text-accent-silver text-sm">
              Monday – Friday: 9am – 5pm<br />
              Saturday – Sunday: Closed
            </p>
          </div>
        </div>

        <div className="bg-surface-coal rounded-2xl border border-surface-slate/30 p-8">
          <h2 className="text-xl font-display font-bold text-accent-white mb-6">Send us a message</h2>
          <form className="space-y-4">
            <div>
              <label className="block text-sm text-accent-silver mb-2">Name</label>
              <input type="text" className="input w-full" placeholder="Your name" />
            </div>
            <div>
              <label className="block text-sm text-accent-silver mb-2">Email</label>
              <input type="email" className="input w-full" placeholder="your@email.com" />
            </div>
            <div>
              <label className="block text-sm text-accent-silver mb-2">Message</label>
              <textarea className="input w-full h-32 resize-none" placeholder="How can we help?" />
            </div>
            <button type="submit" className="btn-primary w-full py-3">
              Send Message
              <IconArrowRight size={18} />
            </button>
          </form>
        </div>
      </div>
    ),
  },
  support: {
    title: 'Support',
    subtitle: 'Get help with your headset',
    content: (
      <div>
        <p className="text-lg text-accent-silver mb-8">
          Need help setting up your headset or troubleshooting an issue?
          We've got you covered with comprehensive support resources.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { title: 'Setup Guides', description: 'Step-by-step instructions for your headset', href: '/setup' },
            { title: 'Compatibility', description: 'Check if your headset works with your devices', href: '/compatibility' },
            { title: 'Warranty Info', description: 'Learn about our warranty coverage', href: '/warranty' },
            { title: 'Returns', description: 'Easy 30-day return process', href: '/returns' },
            { title: 'FAQs', description: 'Answers to common questions', href: '/faq' },
            { title: 'Contact Us', description: 'Speak with our support team', href: '/contact' },
          ].map((item) => (
            <a
              key={item.title}
              href={item.href}
              className="group p-6 bg-surface-coal rounded-xl border border-surface-slate/30 hover:border-brand-orange/50 transition-all duration-300"
            >
              <h3 className="font-semibold text-accent-white group-hover:text-brand-orange transition-colors">
                {item.title}
              </h3>
              <p className="text-sm text-accent-silver mt-2">{item.description}</p>
              <span className="inline-flex items-center gap-1 text-sm text-brand-orange mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more <IconArrowRight size={14} />
              </span>
            </a>
          ))}
        </div>
      </div>
    ),
  },

  // ============================================================================
  // SHIPPING PAGE - REDESIGNED
  // ============================================================================
  shipping: {
    title: 'Shipping & Delivery',
    subtitle: 'Fast, tracked delivery across the UK',
    content: (
      <div className="space-y-10">
        {/* Last Updated Badge */}
        <div className="flex items-center gap-2 text-xs text-accent-silver">
          <IconClock size={14} />
          <span>Last updated: January 2025</span>
        </div>

        {/* Key Stats */}
        <KeyStats stats={[
          { value: 'Free', label: 'Over £75' },
          { value: 'Next Day', label: 'Before 3pm' },
          { value: '100%', label: 'Tracked' },
          { value: 'UK Wide', label: 'Coverage' },
        ]} />

        {/* Highlight Cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <HighlightCard
            icon={IconShippingFast}
            title="Free Next-Day"
            description="Orders over £50 qualify for free next-day delivery to UK mainland"
            variant="success"
          />
          <HighlightCard
            icon={IconPackage}
            title="Tracked Delivery"
            description="Every order includes full tracking so you always know where it is"
          />
          <HighlightCard
            icon={IconReturn}
            title="Easy Returns"
            description="Changed your mind? Return within 30 days for a full refund"
          />
        </div>

        {/* Delivery Options Table */}
        <div className="space-y-4">
          <h2 className="text-xl font-display font-bold text-accent-white">Delivery Options & Costs</h2>
          <p className="text-accent-silver">All prices include VAT. Delivery available to UK addresses.</p>

          <PolicyTable
            headers={['Service', 'Delivery Time', 'Cost']}
            rows={[
              ['Standard Delivery', '2-3 business days', '£4.99 (Free over £50)'],
              ['Next-Day Delivery', 'Next business day*', '£6.99 (Free over £75)'],
              ['Scottish Highlands & Islands', '3-5 business days', '£9.99'],
            ]}
          />

          <InfoBox variant="info">
            <p>* Order before 3pm Mon-Fri for next business day delivery</p>
          </InfoBox>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-3">
          <PolicyAccordion title="Order Processing" icon={IconPackage} defaultOpen>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem><strong>Order confirmation:</strong> You'll receive an email immediately after placing your order</ChecklistItem>
              <ChecklistItem><strong>Dispatch notification:</strong> We'll email you tracking details when your order ships</ChecklistItem>
              <ChecklistItem><strong>Processing time:</strong> Most orders are dispatched within 24 hours (Mon-Fri)</ChecklistItem>
              <ChecklistItem><strong>Weekend orders:</strong> Orders placed after 3pm Friday ship on Monday</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion title="Tracking Your Order" icon={IconEye}>
            <p>All orders include tracking. Once dispatched, you'll receive an email with a tracking link. You can also track your order by logging into your account or contacting our support team.</p>
          </PolicyAccordion>

          <PolicyAccordion title="Delivery Partners" icon={IconShippingFast}>
            <p className="mb-3">We use trusted delivery partners including:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem><strong>Royal Mail</strong> — Standard delivery</ChecklistItem>
              <ChecklistItem><strong>DPD</strong> — Next-day delivery</ChecklistItem>
              <ChecklistItem><strong>UPS</strong> — Express delivery</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion title="Delivery Address Requirements" icon={IconMapPin}>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>Ensure your delivery address is complete and accurate</ChecklistItem>
              <ChecklistItem>Include any apartment/unit numbers and access codes</ChecklistItem>
              <ChecklistItem>We deliver to home addresses, workplaces, and PO Boxes</ChecklistItem>
              <ChecklistItem>Someone must be available to sign for next-day and express deliveries</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion title="Missing or Delayed Orders" icon={IconAlertCircle}>
            <p className="mb-3">If your order hasn't arrived within the expected timeframe:</p>
            <ProcessSteps steps={[
              { title: 'Check tracking', description: 'Use your tracking link for the latest status' },
              { title: 'Check with neighbours', description: 'It may have been left safely nearby' },
              { title: 'Contact support', description: 'Get in touch with your order number' },
            ]} />
            <p className="mt-4">We'll investigate and arrange a replacement or refund if your order is lost.</p>
          </PolicyAccordion>

          <PolicyAccordion title="International Shipping" icon={IconGlobe}>
            <p>We currently only ship to UK addresses. For international orders, please contact us at sales@itremarketing.co.uk to discuss options.</p>
          </PolicyAccordion>
        </div>

        {/* Contact Section */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-surface-coal to-surface-graphite border border-surface-slate/30">
          <h3 className="font-semibold text-accent-white mb-4">Shipping Enquiries</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <ContactCard icon={IconMail} label="Email" value="sales@itremarketing.co.uk" href="mailto:sales@itremarketing.co.uk" />
            <ContactCard icon={IconPhone} label="Phone" value="020 4591 5420" href="tel:02045915420" />
          </div>
          <p className="text-xs text-accent-silver mt-4">Mon-Fri, 9am-5pm</p>
        </div>
      </div>
    ),
  },

  // ============================================================================
  // RETURNS PAGE - REDESIGNED
  // ============================================================================
  returns: {
    title: 'Returns & Refunds',
    subtitle: '30-day hassle-free returns with free return shipping',
    content: (
      <div className="space-y-10">
        {/* Last Updated Badge */}
        <div className="flex items-center gap-2 text-xs text-accent-silver">
          <IconClock size={14} />
          <span>Last updated: January 2025</span>
        </div>

        {/* Key Stats */}
        <KeyStats stats={[
          { value: '30', label: 'Day Returns' },
          { value: 'Free', label: 'Return Shipping' },
          { value: '5-7', label: 'Days to Refund' },
          { value: '100%', label: 'Money Back' },
        ]} />

        {/* Intro */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/5 to-transparent border border-emerald-500/20">
          <p className="text-lg text-accent-platinum leading-relaxed">
            We want you to be completely satisfied with your purchase. If you're not happy for any
            reason, we offer a simple <strong className="text-emerald-400">30-day return policy</strong> on all products — double the legal requirement.
          </p>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-3">
          <PolicyAccordion title="Your Cancellation Rights" icon={IconFileText} defaultOpen>
            <p>Under the <strong>Consumer Contracts Regulations 2013</strong>, you have the right to cancel your order within 14 days of receiving your goods without giving any reason.</p>
            <InfoBox variant="tip" title="Extended Returns">
              We extend this to <strong>30 days</strong> as part of our customer satisfaction guarantee — giving you double the time to decide.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion title="How to Return an Item" icon={IconReturn}>
            <ProcessSteps steps={[
              { title: 'Contact us', description: 'Email sales@itremarketing.co.uk or call 020 4591 5420 with your order number' },
              { title: 'Receive return label', description: "We'll email you a free prepaid return label within 24 hours" },
              { title: 'Pack securely', description: 'Place the item in its original packaging with all accessories' },
              { title: 'Ship it', description: 'Drop off at any Royal Mail or courier collection point' },
              { title: 'Get your refund', description: "We'll process your refund within 5-7 business days of receiving the item" },
            ]} />
          </PolicyAccordion>

          <PolicyAccordion title="Return Conditions" icon={IconCheck}>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>Items must be returned within 30 days of delivery date</ChecklistItem>
              <ChecklistItem>Products must be unused and in original condition</ChecklistItem>
              <ChecklistItem>All accessories, manuals, and packaging must be included</ChecklistItem>
              <ChecklistItem>Items must not be damaged beyond normal inspection</ChecklistItem>
              <ChecklistItem>Proof of purchase (order confirmation email) is required</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion title="Items That Cannot Be Returned" icon={IconAlertCircle}>
            <InfoBox variant="warning" title="Non-Returnable Items">
              <ul className="list-disc pl-4 space-y-1">
                <li>Hygiene-sealed items that have been opened (e.g., ear cushions)</li>
                <li>Personalised or custom-ordered products</li>
                <li>Items damaged through misuse or negligence</li>
              </ul>
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion title="Refund Details" icon={IconCreditCard}>
            <p className="mb-3">Once we receive and inspect your return:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem><strong>Refund amount:</strong> Full purchase price including original delivery cost</ChecklistItem>
              <ChecklistItem><strong>Refund method:</strong> Same payment method used for the original purchase</ChecklistItem>
              <ChecklistItem><strong>Processing time:</strong> 5-7 business days after we receive your return</ChecklistItem>
              <ChecklistItem><strong>Bank processing:</strong> May take an additional 3-5 days to appear in your account</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion title="Exchanges" icon={IconReturn}>
            <p>If you'd like to exchange an item for a different model or variant, please return the original item for a refund and place a new order. This ensures you get the fastest service.</p>
          </PolicyAccordion>

          <PolicyAccordion title="Faulty or Damaged Items" icon={IconShieldCheck}>
            <p className="mb-3">If your item arrives damaged or develops a fault within the warranty period:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>Contact us immediately with photos of the damage/fault</ChecklistItem>
              <ChecklistItem>We'll arrange free collection and replacement or refund</ChecklistItem>
              <ChecklistItem>Your statutory rights under the Consumer Rights Act 2015 are not affected</ChecklistItem>
            </ul>
          </PolicyAccordion>
        </div>

        {/* Contact Section */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-surface-coal to-surface-graphite border border-surface-slate/30">
          <h3 className="font-semibold text-accent-white mb-4">Returns Enquiries</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <ContactCard icon={IconMail} label="Email" value="sales@itremarketing.co.uk" href="mailto:sales@itremarketing.co.uk" />
            <ContactCard icon={IconPhone} label="Phone" value="020 4591 5420" href="tel:02045915420" />
          </div>
          <p className="text-xs text-accent-silver mt-4">Mon-Fri, 9am-5pm</p>
        </div>
      </div>
    ),
  },

  // ============================================================================
  // WARRANTY PAGE - REDESIGNED
  // ============================================================================
  warranty: {
    title: 'Warranty Information',
    subtitle: 'Comprehensive coverage for peace of mind',
    content: (
      <div className="space-y-10">
        {/* Key Stats */}
        <KeyStats stats={[
          { value: '2', label: 'Year Warranty' },
          { value: 'Free', label: 'Replacements' },
          { value: '24hr', label: 'Response Time' },
          { value: '100%', label: 'Coverage' },
        ]} />

        {/* Intro */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-orange/5 to-transparent border border-brand-orange/20">
          <p className="text-lg text-accent-platinum leading-relaxed">
            All products purchased from IT Remarketing come with a minimum <strong className="text-brand-orange">2-year manufacturer warranty</strong>. Some products include extended warranty options for additional peace of mind.
          </p>
        </div>

        {/* Coverage Cards */}
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-accent-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
                <IconCheck size={16} />
              </span>
              What's Covered
            </h3>
            <div className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 space-y-3">
              <ul className="space-y-2 list-none pl-0 text-accent-silver">
                <ChecklistItem>Manufacturing defects</ChecklistItem>
                <ChecklistItem>Component failures under normal use</ChecklistItem>
                <ChecklistItem>Electronic malfunctions</ChecklistItem>
                <ChecklistItem>Battery degradation beyond normal wear</ChecklistItem>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-accent-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center">
                <IconAlertCircle size={16} />
              </span>
              What's Not Covered
            </h3>
            <div className="p-5 rounded-xl bg-red-500/5 border border-red-500/20 space-y-3">
              <ul className="space-y-2 list-none pl-0 text-accent-silver">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>Physical damage from drops or impacts</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>Water damage (unless rated water-resistant)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>Normal wear and tear (ear cushions, cables)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>Damage from improper use</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Claim Process */}
        <div className="space-y-4">
          <h3 className="text-xl font-display font-bold text-accent-white">Making a Warranty Claim</h3>
          <ProcessSteps steps={[
            { title: 'Contact support', description: 'Reach out with your order number and description of the issue' },
            { title: 'Provide details', description: "We may ask for photos or videos to assess the problem" },
            { title: 'Receive resolution', description: "We'll arrange repair, replacement, or refund as appropriate" },
          ]} />
        </div>

        {/* Contact Section */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-surface-coal to-surface-graphite border border-surface-slate/30">
          <h3 className="font-semibold text-accent-white mb-4">Warranty Claims</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <ContactCard icon={IconMail} label="Email" value="sales@itremarketing.co.uk" href="mailto:sales@itremarketing.co.uk" />
            <ContactCard icon={IconPhone} label="Phone" value="020 4591 5420" href="tel:02045915420" />
          </div>
        </div>
      </div>
    ),
  },

  // ============================================================================
  // PRIVACY POLICY - REDESIGNED
  // ============================================================================
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How we collect, use, and protect your personal data under UK GDPR',
    content: (
      <div className="space-y-10">
        {/* Last Updated Badge */}
        <div className="flex items-center gap-2 text-xs text-accent-silver">
          <IconClock size={14} />
          <span>Last updated: February 2026</span>
        </div>

        {/* Intro */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-orange/5 to-transparent border border-brand-orange/20">
          <p className="text-lg text-accent-platinum leading-relaxed">
            IT Remarketing Limited ("we", "us", "our") is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data
            in accordance with the <strong>UK General Data Protection Regulation (UK GDPR)</strong> and
            the <strong>Data Protection Act 2018</strong>.
          </p>
        </div>

        {/* Data Controller Card */}
        <div className="p-5 rounded-xl bg-surface-coal border border-surface-slate/30">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-brand-orange/10 text-brand-orange flex items-center justify-center flex-shrink-0">
              <IconDatabase size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-accent-white mb-2">Data Controller</h3>
              <p className="text-sm text-accent-silver leading-relaxed">
                IT Remarketing Limited<br />
                Swan Ken, Broadbridge Heath Road, Warnham, Horsham, RH12 3PA<br />
                Privacy Contact: <a href="mailto:sales@itremarketing.co.uk" className="text-brand-orange hover:underline">sales@itremarketing.co.uk</a><br />
                Phone: 020 4591 5420<br />
                Company No: 15820855 | VAT No: GB477755043
              </p>
            </div>
          </div>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-3">
          <PolicyAccordion number="1" title="Information We Collect" defaultOpen>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-accent-white mb-2">Personal Data You Provide Directly</h4>
                <ul className="space-y-2 list-none pl-0">
                  <ChecklistItem><strong>Identity Data:</strong> First name, last name</ChecklistItem>
                  <ChecklistItem><strong>Contact Data:</strong> Email address, telephone number, delivery and billing address</ChecklistItem>
                  <ChecklistItem><strong>Financial Data:</strong> Payment card details (processed securely by Shopify Payments — we do not store card numbers)</ChecklistItem>
                  <ChecklistItem><strong>Transaction Data:</strong> Details of products purchased, order history, payment records</ChecklistItem>
                  <ChecklistItem><strong>Communication Data:</strong> Messages you send via our contact form, email, or phone</ChecklistItem>
                  <ChecklistItem><strong>Marketing Preferences:</strong> Whether you have opted in to receive marketing communications</ChecklistItem>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-accent-white mb-2">Personal Data Collected Automatically</h4>
                <ul className="space-y-2 list-none pl-0">
                  <ChecklistItem><strong>Technical Data:</strong> IP address, browser type and version, operating system, time zone setting</ChecklistItem>
                  <ChecklistItem><strong>Usage Data:</strong> Pages visited, time spent on pages, page interaction data, referring URL</ChecklistItem>
                  <ChecklistItem><strong>Cookie Data:</strong> See our <a href="/cookies" className="text-brand-orange hover:underline">Cookie Policy</a> for full details</ChecklistItem>
                </ul>
              </div>
              <InfoBox variant="info" title="Data We Do Not Collect">
                We do not collect any Special Category Data (e.g., health data, biometric data, political opinions, religious beliefs) or criminal conviction data.
              </InfoBox>
            </div>
          </PolicyAccordion>

          <PolicyAccordion number="2" title="Lawful Basis for Processing">
            <p className="mb-3">Under Article 6 of the UK GDPR, we rely on the following lawful bases for processing your personal data:</p>
            <PolicyTable
              headers={['Purpose', 'Lawful Basis', 'Data Used']}
              rows={[
                ['Processing and fulfilling your orders', 'Performance of a contract (Art. 6(1)(b))', 'Identity, Contact, Financial, Transaction'],
                ['Managing your account', 'Performance of a contract (Art. 6(1)(b))', 'Identity, Contact, Transaction'],
                ['Responding to enquiries and support requests', 'Performance of a contract / Legitimate interest (Art. 6(1)(b)/(f))', 'Identity, Contact, Communication'],
                ['Sending order updates (confirmation, dispatch, delivery)', 'Performance of a contract (Art. 6(1)(b))', 'Identity, Contact, Transaction'],
                ['Sending marketing emails and newsletters', 'Consent (Art. 6(1)(a)) — you can withdraw anytime', 'Identity, Contact, Marketing Preferences'],
                ['Website analytics and performance improvement', 'Legitimate interest (Art. 6(1)(f))', 'Technical, Usage, Cookie Data'],
                ['Fraud prevention and website security', 'Legitimate interest (Art. 6(1)(f))', 'Technical, Identity, Transaction'],
                ['Compliance with legal obligations (tax, accounting)', 'Legal obligation (Art. 6(1)(c))', 'Identity, Financial, Transaction'],
              ]}
            />
            <InfoBox variant="info" title="Legitimate Interest Assessment">
              Where we rely on legitimate interests, we have carried out a balancing test to ensure our interests do not override your fundamental rights and freedoms. You may request details of this assessment by contacting us.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion number="3" title="How We Use Your Information">
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem><strong>Order Fulfilment:</strong> Processing, dispatching, and delivering your orders</ChecklistItem>
              <ChecklistItem><strong>Customer Service:</strong> Responding to enquiries, complaints, and support requests</ChecklistItem>
              <ChecklistItem><strong>Transactional Communications:</strong> Order confirmations, shipping updates, delivery notifications, receipts</ChecklistItem>
              <ChecklistItem><strong>Marketing:</strong> Promotional offers, new product announcements, newsletters (only with your explicit opt-in consent under PECR)</ChecklistItem>
              <ChecklistItem><strong>Service Improvement:</strong> Analysing website usage patterns to improve our products and website experience</ChecklistItem>
              <ChecklistItem><strong>Security:</strong> Protecting against fraud, unauthorised access, and other security threats</ChecklistItem>
              <ChecklistItem><strong>Legal Compliance:</strong> Meeting our obligations under tax, consumer protection, and other applicable laws</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion number="4" title="Who We Share Your Data With">
            <p className="mb-3">We share your personal data only with trusted third parties who need it to provide services on our behalf. We do not and will never sell your personal data.</p>
            <PolicyTable
              headers={['Category', 'Provider(s)', 'Purpose', 'Location']}
              rows={[
                ['E-commerce Platform', 'Shopify Inc.', 'Order processing, hosting, payment processing', 'Canada / USA (adequacy decision)'],
                ['Payment Processing', 'Shopify Payments (Stripe)', 'Secure card payment processing', 'UK / EEA'],
                ['Delivery Partners', 'Royal Mail, DPD, UPS', 'Order delivery and tracking', 'UK'],
                ['Analytics', 'Google Analytics (G-YG2J62JYBX)', 'Website usage analysis (anonymised)', 'USA (SCCs in place)'],
                ['Tag Management', 'Google Tag Manager (GTM-T6G35NMJ)', 'Managing website tracking scripts', 'USA (SCCs in place)'],
                ['Email (Transactional)', 'Shopify Email', 'Order confirmations and shipping updates', 'Canada / USA'],
              ]}
            />
            <InfoBox variant="tip" title="Your Data is Protected">
              All third-party processors are contractually obligated to process your data only on our instructions and in compliance with UK GDPR. We have Data Processing Agreements in place with each provider.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion number="5" title="International Data Transfers">
            <p className="mb-3">Some of our service providers are based outside the UK. When we transfer personal data internationally, we ensure it is protected by one of the following safeguards:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem><strong>UK Adequacy Decisions:</strong> Transfers to countries deemed to provide adequate data protection by the UK Secretary of State (e.g., Canada under PIPEDA for commercial activities)</ChecklistItem>
              <ChecklistItem><strong>International Data Transfer Agreement (IDTA):</strong> UK-approved Standard Contractual Clauses for transfers to the USA and other countries</ChecklistItem>
              <ChecklistItem><strong>Supplementary Measures:</strong> Additional technical and organisational safeguards where necessary, such as encryption and pseudonymisation</ChecklistItem>
            </ul>
            <p className="mt-3">You may request a copy of the relevant transfer safeguards by contacting us.</p>
          </PolicyAccordion>

          <PolicyAccordion number="6" title="Data Security">
            <p className="mb-3">We take the security of your personal data seriously and implement appropriate technical and organisational measures, including:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>TLS/SSL encryption (256-bit) for all data in transit</ChecklistItem>
              <ChecklistItem>PCI DSS compliant payment processing via Shopify Payments</ChecklistItem>
              <ChecklistItem>Encrypted data at rest on Shopify's infrastructure</ChecklistItem>
              <ChecklistItem>Access controls and principle of least privilege for staff</ChecklistItem>
              <ChecklistItem>Regular review of security practices and incident response procedures</ChecklistItem>
            </ul>
            <InfoBox variant="warning" title="Data Breach Notification">
              In the event of a personal data breach that poses a risk to your rights and freedoms, we will notify the relevant supervisory authority within 72 hours and inform you without undue delay, as required under Articles 33 and 34 of the UK GDPR.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion number="7" title="Data Retention">
            <p className="mb-3">We retain personal data only for as long as necessary for the purposes set out in this policy. Our specific retention periods are:</p>
            <PolicyTable
              headers={['Data Category', 'Retention Period', 'Reason']}
              rows={[
                ['Order and transaction records', '7 years from date of transaction', 'HMRC tax and accounting obligations'],
                ['Customer account data', 'Until you request deletion or 3 years of inactivity', 'Contract performance and customer service'],
                ['Marketing consent records', 'Until you withdraw consent', 'Compliance with PECR and UK GDPR'],
                ['Contact form messages', '2 years from enquiry date', 'Legitimate interest in resolving queries'],
                ['Website analytics data', '26 months (Google Analytics default)', 'Legitimate interest in service improvement'],
                ['Cookie consent preferences', '12 months', 'PECR compliance'],
              ]}
            />
            <p className="mt-3">When data is no longer needed, it is securely deleted or anonymised.</p>
          </PolicyAccordion>

          <PolicyAccordion number="8" title="Your Rights Under UK GDPR">
            <p className="mb-3">Under UK GDPR and the Data Protection Act 2018, you have the following rights. These rights are not absolute and may be subject to exemptions:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem><strong>Right of Access (Art. 15):</strong> Request a copy of the personal data we hold about you (Subject Access Request). We will respond within one month</ChecklistItem>
              <ChecklistItem><strong>Right to Rectification (Art. 16):</strong> Have inaccurate or incomplete personal data corrected</ChecklistItem>
              <ChecklistItem><strong>Right to Erasure (Art. 17):</strong> Request deletion of your personal data ("right to be forgotten") where there is no compelling reason for continued processing</ChecklistItem>
              <ChecklistItem><strong>Right to Restrict Processing (Art. 18):</strong> Request that we limit how we use your data while a concern is being investigated</ChecklistItem>
              <ChecklistItem><strong>Right to Data Portability (Art. 20):</strong> Receive your personal data in a structured, commonly used, machine-readable format</ChecklistItem>
              <ChecklistItem><strong>Right to Object (Art. 21):</strong> Object to processing based on legitimate interests or for direct marketing purposes</ChecklistItem>
              <ChecklistItem><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, withdraw that consent at any time without affecting the lawfulness of prior processing</ChecklistItem>
            </ul>
            <InfoBox variant="info" title="How to Exercise Your Rights">
              To exercise any of these rights, email us at <a href="mailto:sales@itremarketing.co.uk" className="text-brand-orange hover:underline">sales@itremarketing.co.uk</a> with the subject line "Data Protection Request". We may need to verify your identity before processing your request. We will respond within <strong>one calendar month</strong> as required by law.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion number="9" title="Automated Decision-Making & Profiling">
            <p>We do not carry out any automated decision-making or profiling that produces legal or similarly significant effects on you (as defined under Article 22 of UK GDPR). Any personalisation on our website (e.g., product recommendations) is based on aggregated, non-identifiable usage data and does not constitute profiling under UK GDPR.</p>
          </PolicyAccordion>

          <PolicyAccordion number="10" title="Children's Data">
            <p>Our website and services are not directed at individuals under the age of 16. We do not knowingly collect personal data from children. If you believe we have inadvertently collected data from a child under 16, please contact us immediately and we will take steps to delete it.</p>
          </PolicyAccordion>

          <PolicyAccordion number="11" title="Cookies and Similar Technologies">
            <p className="mb-3">We use cookies and similar technologies in accordance with the <strong>Privacy and Electronic Communications Regulations 2003 (PECR)</strong>. Essential cookies are placed without consent as they are strictly necessary. Non-essential cookies (analytics, marketing) are only placed with your explicit consent.</p>
            <p>For full details, including specific cookies used and how to manage preferences, see our <a href="/cookies" className="text-brand-orange hover:underline">Cookie Policy</a>.</p>
          </PolicyAccordion>

          <PolicyAccordion number="12" title="Third-Party Links">
            <p>Our website may contain links to third-party websites (e.g., manufacturer sites, social media). We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies before providing any personal data.</p>
          </PolicyAccordion>

          <PolicyAccordion number="13" title="Changes to This Policy">
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. Where changes are significant, we will notify you via email (if you have an account) or by a prominent notice on our website. The "last updated" date at the top of this policy indicates when it was last revised.</p>
          </PolicyAccordion>

          <PolicyAccordion number="14" title="Contact Us & Complaints">
            <p className="mb-3">For any privacy-related enquiries, to exercise your data rights, or to raise a concern:</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <ContactCard icon={IconMail} label="Email" value="sales@itremarketing.co.uk" href="mailto:sales@itremarketing.co.uk" />
              <ContactCard icon={IconPhone} label="Phone" value="020 4591 5420" href="tel:02045915420" />
            </div>
            <p className="text-sm mb-4">Post: IT Remarketing Limited, Swan Ken, Broadbridge Heath Road, Warnham, Horsham, RH12 3PA</p>
          </PolicyAccordion>
        </div>
      </div>
    ),
  },

  // ============================================================================
  // TERMS & CONDITIONS - REDESIGNED
  // ============================================================================
  terms: {
    title: 'Terms & Conditions',
    subtitle: 'Terms of use for our website and services',
    content: (
      <div className="space-y-10">
        {/* Last Updated Badge */}
        <div className="flex items-center gap-2 text-xs text-accent-silver">
          <IconClock size={14} />
          <span>Last updated: January 2025</span>
        </div>

        {/* Intro */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-orange/5 to-transparent border border-brand-orange/20">
          <p className="text-lg text-accent-platinum leading-relaxed">
            These Terms and Conditions govern your use of www.itremarketing.co.uk operated by
            IT Remarketing Limited. By using this website or placing an order, you agree to be bound by these terms.
          </p>
        </div>

        {/* Company Info Card */}
        <div className="p-5 rounded-xl bg-surface-coal border border-surface-slate/30">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-brand-orange/10 text-brand-orange flex items-center justify-center flex-shrink-0">
              <IconFileText size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-accent-white mb-2">Company Information</h3>
              <p className="text-sm text-accent-silver leading-relaxed">
                IT Remarketing Limited<br />
                Registered in England and Wales: Company No. 15820855<br />
                VAT Registration: GB477755043<br />
                Registered Office: Swan Ken, Broadbridge Heath Road, Warnham, Horsham, RH12 3PA<br />
                Email: sales@itremarketing.co.uk | Phone: 020 4591 5420
              </p>
            </div>
          </div>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-3">
          <PolicyAccordion number="1" title="Definitions" defaultOpen>
            <ul className="space-y-2 list-none pl-0">
              <li><strong className="text-accent-white">"We/Us/Our"</strong> — refers to IT Remarketing Limited</li>
              <li><strong className="text-accent-white">"You/Your"</strong> — refers to the customer or website user</li>
              <li><strong className="text-accent-white">"Products"</strong> — refers to items available for purchase</li>
              <li><strong className="text-accent-white">"Website"</strong> — refers to www.itremarketing.co.uk</li>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion number="2" title="Website Use">
            <p className="mb-3">This website is provided for your personal, non-commercial use. You agree not to:</p>
            <ul className="space-y-2 list-none pl-0">
              <li className="flex items-start gap-3">
                <span className="text-red-400 mt-1">✕</span>
                <span>Use the website for any unlawful purpose</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400 mt-1">✕</span>
                <span>Attempt to gain unauthorised access to any part of the website</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400 mt-1">✕</span>
                <span>Reproduce, duplicate, or copy any material without permission</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400 mt-1">✕</span>
                <span>Use automated systems to extract data from the website</span>
              </li>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion number="3" title="Products and Pricing">
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>All prices are displayed in GBP (£) and include VAT</ChecklistItem>
              <ChecklistItem>We make every effort to ensure accuracy but reserve the right to correct errors</ChecklistItem>
              <ChecklistItem>Prices may change without notice; the price at time of order applies</ChecklistItem>
              <ChecklistItem>Product images are for illustration; actual products may vary slightly</ChecklistItem>
              <ChecklistItem>We reserve the right to limit quantities or refuse orders</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion number="4" title="Orders and Contracts">
            <p className="mb-3">When you place an order:</p>
            <ProcessSteps steps={[
              { title: 'Your order is an offer', description: 'You are offering to buy from us' },
              { title: 'Order acknowledgement', description: "We'll send an email (this is not acceptance)" },
              { title: 'Contract formed', description: 'A contract is formed only when we dispatch your order' },
              { title: 'Order refusal', description: 'We reserve the right to refuse any order' },
            ]} />
          </PolicyAccordion>

          <PolicyAccordion number="5" title="Payment">
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>We accept Visa, Mastercard, American Express, PayPal, and Apple Pay</ChecklistItem>
              <ChecklistItem>Payment is taken at the time of order</ChecklistItem>
              <ChecklistItem>All payments are processed securely through PCI DSS compliant systems</ChecklistItem>
              <ChecklistItem>Your card will be charged in GBP</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion number="6" title="Delivery">
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>We deliver to addresses within the United Kingdom</ChecklistItem>
              <ChecklistItem>Delivery times are estimates and not guaranteed</ChecklistItem>
              <ChecklistItem>Risk of loss passes to you upon delivery</ChecklistItem>
            </ul>
            <p className="mt-3">See our <a href="/shipping" className="text-brand-orange hover:underline">Shipping Policy</a> for full details.</p>
          </PolicyAccordion>

          <PolicyAccordion number="7" title="Returns and Refunds">
            <p>Under the <strong>Consumer Contracts Regulations 2013</strong>, you have the right to cancel your order within 14 days of receiving your goods without giving any reason.</p>
            <p className="mt-3">See our <a href="/returns" className="text-brand-orange hover:underline">Returns Policy</a> for full details including how to return items and receive a refund.</p>
          </PolicyAccordion>

          <PolicyAccordion number="8" title="Warranty">
            <p>All products come with a minimum <strong>2-year manufacturer warranty</strong> covering defects in materials and workmanship. This warranty is in addition to your statutory rights under the Consumer Rights Act 2015.</p>
          </PolicyAccordion>

          <PolicyAccordion number="9" title="Limitation of Liability">
            <p className="mb-3">To the extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from the use of our products or website. Our total liability shall not exceed the purchase price of the relevant product.</p>
            <InfoBox variant="info">
              Nothing in these terms excludes or limits our liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion number="10" title="Intellectual Property">
            <p>All content on this website, including text, graphics, logos, images, and software, is the property of IT Remarketing Limited or its licensors and is protected by copyright and trademark laws. You may not reproduce, distribute, or use any content without our prior written consent.</p>
          </PolicyAccordion>

          <PolicyAccordion number="11" title="Third-Party Links">
            <p>Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of these external sites.</p>
          </PolicyAccordion>

          <PolicyAccordion number="12" title="Privacy">
            <p>Your use of our website is also governed by our <a href="/privacy" className="text-brand-orange hover:underline">Privacy Policy</a>, which explains how we collect, use, and protect your personal data.</p>
          </PolicyAccordion>

          <PolicyAccordion number="13" title="Changes to Terms">
            <p>We may update these terms from time to time. Changes will be posted on this page with an updated revision date. Continued use of the website constitutes acceptance of the modified terms.</p>
          </PolicyAccordion>

          <PolicyAccordion number="14" title="Governing Law">
            <InfoBox variant="info" title="Jurisdiction">
              These terms are governed by the laws of <strong>England and Wales</strong>. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion number="15" title="Contact Us">
            <div className="grid sm:grid-cols-2 gap-4">
              <ContactCard icon={IconMail} label="Email" value="sales@itremarketing.co.uk" href="mailto:sales@itremarketing.co.uk" />
              <ContactCard icon={IconPhone} label="Phone" value="020 4591 5420" href="tel:02045915420" />
            </div>
            <p className="mt-4 text-sm">Post: IT Remarketing Limited, Swan Ken, Broadbridge Heath Road, Warnham, Horsham, RH12 3PA</p>
          </PolicyAccordion>
        </div>
      </div>
    ),
  },

  // ============================================================================
  // COOKIE POLICY - REDESIGNED
  // ============================================================================
  cookies: {
    title: 'Cookie Policy',
    subtitle: 'How we use cookies on our website',
    content: (
      <div className="space-y-10">
        {/* Intro */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-orange/5 to-transparent border border-brand-orange/20">
          <p className="text-lg text-accent-platinum leading-relaxed">
            This policy explains how we use cookies and similar technologies
            to improve your experience on our website. We value your privacy and give you control over your preferences.
          </p>
        </div>

        {/* Cookie Types Grid */}
        <div className="grid sm:grid-cols-2 gap-4">
          <HighlightCard
            icon={IconLock}
            title="Essential Cookies"
            description="Required for the website to function. Enable core features like shopping cart and secure checkout."
            variant="success"
          />
          <HighlightCard
            icon={IconEye}
            title="Analytics Cookies"
            description="Help us understand how visitors interact with our website to improve our services."
          />
          <HighlightCard
            icon={IconCreditCard}
            title="Functional Cookies"
            description="Remember your preferences like language and region to personalise your experience."
          />
          <HighlightCard
            icon={IconGlobe}
            title="Marketing Cookies"
            description="With your consent, show relevant advertisements and measure their effectiveness."
            variant="warning"
          />
        </div>

        {/* Accordion Sections */}
        <div className="space-y-3">
          <PolicyAccordion title="What Are Cookies?" icon={IconCookie} defaultOpen>
            <p>Cookies are small text files stored on your device when you visit a website. They help us remember your preferences and understand how you use our site. Cookies can be "session" cookies (deleted when you close your browser) or "persistent" cookies (remain until they expire or you delete them).</p>
          </PolicyAccordion>

          <PolicyAccordion title="Essential Cookies" icon={IconLock}>
            <p className="mb-3">These cookies are necessary for the website to function properly and cannot be disabled. They enable:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>Shopping cart functionality</ChecklistItem>
              <ChecklistItem>Secure checkout process</ChecklistItem>
              <ChecklistItem>User authentication</ChecklistItem>
              <ChecklistItem>Cookie consent preferences</ChecklistItem>
            </ul>
          </PolicyAccordion>

          <PolicyAccordion title="Analytics Cookies" icon={IconEye}>
            <p className="mb-3">We use analytics cookies to understand how visitors interact with our website. This helps us improve our site and provide better products and services.</p>
            <InfoBox variant="info" title="Google Analytics">
              We use Google Analytics to collect anonymised data about page views, traffic sources, and user behaviour. This data is aggregated and does not personally identify you.
            </InfoBox>
          </PolicyAccordion>

          <PolicyAccordion title="Marketing Cookies" icon={IconGlobe}>
            <p className="mb-3">With your consent, we may use marketing cookies to:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>Show you relevant advertisements on other websites</ChecklistItem>
              <ChecklistItem>Measure the effectiveness of our advertising campaigns</ChecklistItem>
              <ChecklistItem>Limit the number of times you see an advertisement</ChecklistItem>
            </ul>
            <p className="mt-3">You can withdraw consent for marketing cookies at any time.</p>
          </PolicyAccordion>

          <PolicyAccordion title="Managing Cookies" icon={IconShieldCheck}>
            <p className="mb-3">You can control cookies through your browser settings:</p>
            <ul className="space-y-2 list-none pl-0">
              <ChecklistItem>View and delete existing cookies</ChecklistItem>
              <ChecklistItem>Block all or certain types of cookies</ChecklistItem>
              <ChecklistItem>Set preferences for specific websites</ChecklistItem>
            </ul>
            <InfoBox variant="warning" title="Important">
              Disabling certain cookies may affect your experience on our website. Essential cookies cannot be disabled as they are required for basic functionality.
            </InfoBox>
          </PolicyAccordion>
        </div>

        {/* Contact Section */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-surface-coal to-surface-graphite border border-surface-slate/30">
          <h3 className="font-semibold text-accent-white mb-4">Questions About Cookies?</h3>
          <ContactCard icon={IconMail} label="Email" value="sales@itremarketing.co.uk" href="mailto:sales@itremarketing.co.uk" />
        </div>
      </div>
    ),
  },

  // ============================================================================
  // OTHER PAGES (keeping existing structure)
  // ============================================================================
  compatibility: {
    title: 'Compatibility Guide',
    subtitle: 'Check if your headset works with your devices',
    content: (
      <div>
        <p className="text-lg text-accent-silver mb-8">
          Our headsets are compatible with a wide range of devices and software.
          Use this guide to ensure the perfect match for your setup.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {[
            {
              title: 'Microsoft Teams',
              description: 'Look for "Teams Certified" badges. These headsets offer plug-and-play functionality with dedicated Teams button.',
              compatible: ['Jabra Evolve2 Series', 'Poly Voyager Focus 2', 'Axtel Prime HD Series'],
            },
            {
              title: 'Zoom',
              description: 'Zoom-certified headsets provide optimized audio and easy controls for Zoom meetings.',
              compatible: ['Jabra Evolve2 75', 'Poly Voyager 4320', 'Jabra Speak Series'],
            },
            {
              title: 'Softphones & VoIP',
              description: 'USB headsets work with most softphone applications including Cisco, Avaya, and RingCentral.',
              compatible: ['All USB headsets', 'DECT wireless systems', 'Bluetooth with USB dongle'],
            },
            {
              title: 'Mobile Devices',
              description: 'Bluetooth headsets pair with smartphones and tablets for calls on the go.',
              compatible: ['All Bluetooth headsets', 'Jabra Talk Series', 'Poly Voyager Series'],
            },
          ].map((item) => (
            <div key={item.title} className="p-6 bg-surface-coal rounded-xl border border-surface-slate/30">
              <h3 className="text-xl font-semibold text-accent-white mb-2">{item.title}</h3>
              <p className="text-accent-silver text-sm mb-4">{item.description}</p>
              <div className="text-sm">
                <span className="text-brand-orange font-medium">Recommended: </span>
                <span className="text-accent-platinum">{item.compatible.join(', ')}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="p-6 bg-brand-orange/10 border border-brand-orange/20 rounded-xl">
          <h3 className="font-semibold text-accent-white mb-2">Need Help?</h3>
          <p className="text-accent-silver text-sm mb-4">
            Not sure which headset is right for your setup? Our team can help you find the perfect match.
          </p>
          <a href="/contact" className="text-brand-orange hover:text-brand-orange-light font-medium text-sm flex items-center gap-1">
            Contact Our Experts <IconArrowRight size={14} />
          </a>
        </div>
      </div>
    ),
  },
  setup: {
    title: 'Setup Instructions',
    subtitle: 'Get your headset working in minutes',
    content: (
      <div>
        <p className="text-lg text-accent-silver mb-8">
          Follow these simple steps to set up your new headset.
          Most headsets are plug-and-play, but here are some tips for the best experience.
        </p>

        <div className="space-y-6 mb-12">
          {[
            {
              step: 1,
              title: 'Charge Your Headset',
              description: 'For wireless headsets, fully charge before first use. This typically takes 2-3 hours. USB headsets can be used immediately.',
            },
            {
              step: 2,
              title: 'Connect to Your Device',
              description: 'USB headsets: Simply plug into any USB port. Bluetooth: Enable pairing mode (usually hold power button for 3 seconds) and connect via your device\'s Bluetooth settings.',
            },
            {
              step: 3,
              title: 'Install Software (Optional)',
              description: 'For advanced features, install the manufacturer\'s software: Jabra Direct for Jabra, Poly Lens for Poly, or Axtel Configuration Tool for Axtel headsets.',
            },
            {
              step: 4,
              title: 'Configure Audio Settings',
              description: 'Set your headset as the default audio device in your computer\'s sound settings and your communication app (Teams, Zoom, etc.).',
            },
            {
              step: 5,
              title: 'Test Your Setup',
              description: 'Make a test call to ensure both the speaker and microphone are working correctly. Adjust volume and microphone levels as needed.',
            },
          ].map((item) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-10 h-10 bg-brand-orange rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-white">
                {item.step}
              </div>
              <div>
                <h3 className="font-semibold text-accent-white mb-1">{item.title}</h3>
                <p className="text-accent-silver text-sm">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <a href="/support" className="p-4 bg-surface-coal rounded-xl border border-surface-slate/30 hover:border-brand-orange/30 transition-colors">
            <h4 className="font-medium text-accent-white mb-1">Troubleshooting</h4>
            <p className="text-sm text-accent-silver">Having issues? Check our support guides.</p>
          </a>
          <a href="/contact" className="p-4 bg-surface-coal rounded-xl border border-surface-slate/30 hover:border-brand-orange/30 transition-colors">
            <h4 className="font-medium text-accent-white mb-1">Contact Support</h4>
            <p className="text-sm text-accent-silver">Speak with our technical team.</p>
          </a>
        </div>
      </div>
    ),
  },
  blog: {
    title: 'Blog',
    subtitle: 'News, tips, and insights from our team',
    content: (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-surface-coal rounded-2xl flex items-center justify-center mx-auto mb-6">
          <IconHeadset size={40} className="text-brand-orange" />
        </div>
        <h3 className="text-2xl font-display font-bold text-accent-white mb-4">Coming Soon</h3>
        <p className="text-accent-silver mb-8 max-w-md mx-auto">
          We're working on bringing you expert insights, product reviews, and tips for getting
          the most out of your headsets. Check back soon!
        </p>
        <a href="/collections/all-products" className="btn-primary">
          Shop Headsets
          <IconArrowRight size={18} />
        </a>
      </div>
    ),
  },
  careers: {
    title: 'Careers',
    subtitle: 'Join our growing team',
    content: (
      <div>
        <p className="text-lg text-accent-silver mb-8">
          We're always looking for passionate people to join the IT Remarketing team.
          If you love technology and helping customers, we'd love to hear from you.
        </p>

        <div className="mb-12">
          <h2 className="text-2xl font-display font-bold text-accent-white mb-6">Why Work With Us?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Flexible Working', description: 'Remote-friendly with flexible hours' },
              { title: 'Growth Opportunities', description: 'Learn and develop your career' },
              { title: 'Great Team', description: 'Collaborative and supportive culture' },
              { title: 'Competitive Pay', description: 'Fair compensation and benefits' },
            ].map((item) => (
              <div key={item.title} className="p-4 bg-surface-coal rounded-xl border border-surface-slate/30">
                <h4 className="font-medium text-accent-white mb-1">{item.title}</h4>
                <p className="text-sm text-accent-silver">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 bg-brand-orange/10 border border-brand-orange/20 rounded-xl text-center">
          <h3 className="font-semibold text-accent-white mb-2">No Current Openings</h3>
          <p className="text-accent-silver text-sm mb-4">
            We don't have any positions available right now, but we're always interested in hearing from talented people.
          </p>
          <a href="/contact" className="text-brand-orange hover:text-brand-orange-light font-medium">
            Send us your CV →
          </a>
        </div>
      </div>
    ),
  },
  partners: {
    title: 'Business Enquiries',
    subtitle: 'Bulk and business orders with IT Remarketing',
    content: (
      <div>
        <p className="text-lg text-accent-silver mb-8">
          Looking to equip your team with professional headsets?
          We offer competitive pricing on bulk and business orders.
        </p>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            {
              title: 'Bulk Orders',
              description: 'Competitive pricing on volume orders with dedicated support.',
              icon: IconShippingFast,
            },
            {
              title: 'Business Accounts',
              description: 'Set up a business account for streamlined ordering and invoicing.',
              icon: IconSupport,
            },
            {
              title: 'Enterprise Solutions',
              description: 'Custom solutions for large organisations with volume discounts and dedicated support.',
              icon: IconShieldCheck,
            },
          ].map((item) => (
            <div key={item.title} className="p-6 bg-surface-coal rounded-xl border border-surface-slate/30">
              <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-4">
                <item.icon size={24} className="text-brand-orange" />
              </div>
              <h3 className="font-semibold text-accent-white mb-2">{item.title}</h3>
              <p className="text-sm text-accent-silver">{item.description}</p>
            </div>
          ))}
        </div>

        <div className="p-6 bg-surface-coal rounded-xl border border-surface-slate/30">
          <h3 className="font-semibold text-accent-white mb-2">Get in Touch</h3>
          <p className="text-accent-silver text-sm mb-4">
            Interested in a business order? Get in touch and we'll discuss how we can help.
          </p>
          <a href="/contact" className="btn-primary inline-flex">
            Contact Sales Team
            <IconArrowRight size={16} />
          </a>
        </div>
      </div>
    ),
  },
};

// Default page for unknown slugs
const defaultPage = {
  title: 'Page Coming Soon',
  subtitle: 'We\'re working on this page',
  content: (
    <div className="text-center py-12">
      <div className="w-20 h-20 bg-surface-coal rounded-2xl flex items-center justify-center mx-auto mb-6">
        <IconHeadset size={40} className="text-brand-orange" />
      </div>
      <p className="text-accent-silver mb-8 max-w-md mx-auto">
        This page is currently under construction. Check back soon or explore our products.
      </p>
      <a href="/collections/all-products" className="btn-primary">
        Shop Headsets
        <IconArrowRight size={18} />
      </a>
    </div>
  ),
};

export function InfoPage({ slug }: InfoPageProps) {
  const [shopifyPage, setShopifyPage] = useState<ShopifyPage | null>(null);

  // Fetch page content from Shopify (client-side only)
  useEffect(() => {
    let cancelled = false;

    getPageByHandle(slug)
      .then((page) => {
        if (!cancelled) {
          setShopifyPage(page);
        }
      })
      .catch(() => {
        // Fallback content will be used
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Use Shopify page content if available, otherwise fall back to hardcoded
  const fallbackPage = pageContent[slug] || defaultPage;
  const hasShopifyContent = shopifyPage?.body && shopifyPage.body.trim().length > 0;

  // Sidebar links based on page type
  const getSidebarLinks = () => {
    const policyPages = ['privacy', 'terms', 'cookies'];
    const supportPages = ['shipping', 'returns', 'warranty'];

    if (policyPages.includes(slug)) {
      return [
        { label: 'Privacy Policy', href: '/privacy', active: slug === 'privacy' },
        { label: 'Terms & Conditions', href: '/terms', active: slug === 'terms' },
        { label: 'Cookie Policy', href: '/cookies', active: slug === 'cookies' },
      ];
    }
    if (supportPages.includes(slug)) {
      return [
        { label: 'Shipping Information', href: '/shipping', active: slug === 'shipping' },
        { label: 'Returns & Refunds', href: '/returns', active: slug === 'returns' },
        { label: 'Warranty Information', href: '/warranty', active: slug === 'warranty' },
      ];
    }
    return [];
  };

  const sidebarLinks = getSidebarLinks();

  return (
    <Layout>
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-surface-coal to-surface-black border-b border-surface-slate/30">
        <div className="container-wide py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Breadcrumbs */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <a href="/" className="text-accent-silver hover:text-accent-white transition-colors">
                    Home
                  </a>
                </li>
                <li className="text-accent-silver">/</li>
                <li className="text-brand-orange">
                  {hasShopifyContent ? shopifyPage!.title : fallbackPage.title}
                </li>
              </ol>
            </nav>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-accent-white mb-4">
              {hasShopifyContent ? shopifyPage!.title : fallbackPage.title}
            </h1>
            {!hasShopifyContent && fallbackPage.subtitle && (
              <p className="text-xl text-accent-silver max-w-2xl">{fallbackPage.subtitle}</p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="container-wide py-12 lg:py-16">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-8"
          >
            {hasShopifyContent ? (
              // Render Shopify page HTML content
              <div
                className="prose prose-invert prose-lg max-w-none prose-headings:font-display prose-headings:text-accent-white prose-p:text-accent-silver prose-li:text-accent-silver prose-a:text-brand-orange prose-a:no-underline hover:prose-a:underline"
                dangerouslySetInnerHTML={{ __html: shopifyPage!.body }}
              />
            ) : (
              // Render fallback JSX content
              fallbackPage.content
            )}
          </motion.div>

          {/* Sidebar */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-4"
          >
            <div className="sticky top-24 space-y-6">
              {/* Related Pages */}
              {sidebarLinks.length > 0 && (
                <div className="p-6 bg-surface-coal rounded-2xl border border-surface-slate/30">
                  <h3 className="text-sm font-semibold text-accent-white mb-4">Related Pages</h3>
                  <ul className="space-y-2">
                    {sidebarLinks.map((link) => (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          className={`flex items-center gap-2 py-2 px-3 rounded-lg text-sm transition-colors ${
                            link.active
                              ? 'bg-brand-orange/10 text-brand-orange'
                              : 'text-accent-silver hover:text-accent-white hover:bg-surface-slate/50'
                          }`}
                        >
                          <IconArrowRight size={14} />
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Need Help Card */}
              <div className="p-6 bg-gradient-to-br from-brand-orange/10 to-transparent rounded-2xl border border-brand-orange/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-brand-orange/20 rounded-xl flex items-center justify-center">
                    <IconSupport size={20} className="text-brand-orange" />
                  </div>
                  <h3 className="font-semibold text-accent-white">Need Help?</h3>
                </div>
                <p className="text-sm text-accent-silver mb-4">
                  Our support team is here to assist you with any questions.
                </p>
                <a href="/contact" className="btn-primary w-full justify-center text-sm">
                  Contact Us
                  <IconArrowRight size={16} />
                </a>
              </div>

              {/* Quick Links */}
              <div className="p-6 bg-surface-coal rounded-2xl border border-surface-slate/30">
                <h3 className="text-sm font-semibold text-accent-white mb-4">Quick Links</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="/products" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-2">
                      <IconArrowRight size={14} />
                      Shop All Headsets
                    </a>
                  </li>
                  <li>
                    <a href="/support" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-2">
                      <IconArrowRight size={14} />
                      Support Centre
                    </a>
                  </li>
                  <li>
                    <a href="/faq" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-2">
                      <IconArrowRight size={14} />
                      FAQs
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </Layout>
  );
}
